// <OrgDensityToggle> — Org View Step 8 (T-054, spec §6.1.3).
//
// Three-button segment of icon-only buttons matching the View toggle:
//   [ Compact | Comfortable | Spacious ]
//
// Defaults to Comfortable. Card grid responds to the choice with
// different padding, avatar size, and xl: column count (see
// `densityClasses` below). When the View toggle is set to Table, this
// toggle becomes inactive (table has its own row height).

import { Rows3, LayoutGrid, LayoutDashboard } from 'lucide-react';

export type OrgDensity = 'compact' | 'comfortable' | 'spacious';

const ICONS: Record<OrgDensity, React.ReactNode> = {
  compact: <Rows3 size={16} aria-hidden />,
  comfortable: <LayoutGrid size={16} aria-hidden />,
  spacious: <LayoutDashboard size={16} aria-hidden />,
};

const LABEL: Record<OrgDensity, string> = {
  compact: 'Compact',
  comfortable: 'Comfortable',
  spacious: 'Spacious',
};

export interface OrgDensityToggleProps {
  value: OrgDensity;
  onChange: (next: OrgDensity) => void;
  /** True when the toggle is muted (Table view ignores density). */
  disabled?: boolean;
}

export function OrgDensityToggle({
  value,
  onChange,
  disabled = false,
}: OrgDensityToggleProps) {
  return (
    <div
      role="group"
      aria-label="Density"
      data-testid="org-density-toggle"
      className={`inline-flex items-center bg-slate-100 rounded-lg p-1 ${
        disabled ? 'opacity-40 pointer-events-none' : ''
      }`}
    >
      {(Object.keys(LABEL) as OrgDensity[]).map((key) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          title={LABEL[key]}
          aria-label={LABEL[key]}
          data-testid={`org-density-toggle-${key}`}
          className={`px-2 py-1.5 rounded-md transition-colors ${
            value === key
              ? 'bg-white shadow-sm text-slate-800'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {ICONS[key]}
        </button>
      ))}
    </div>
  );
}

// Density-driven Tailwind class snippets — consumed by OrgCardGrid +
// OrgEmployeeCard. Exposed so unit tests can pin the contract.
export const DENSITY_CLASSES: Record<
  OrgDensity,
  {
    grid: string;
    cardPadding: string;
    avatarSize: string;
    avatarText: string;
  }
> = {
  compact: {
    grid: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
    cardPadding: 'p-4',
    avatarSize: 'w-14 h-14',
    avatarText: 'text-base',
  },
  comfortable: {
    grid: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    cardPadding: 'p-6',
    avatarSize: 'w-16 h-16',
    avatarText: 'text-xl',
  },
  spacious: {
    grid: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3',
    cardPadding: 'p-8',
    avatarSize: 'w-20 h-20',
    avatarText: 'text-2xl',
  },
};
